import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { COMPANY, SERVICES, STAGES, REVENUES, REFERRERS } from "../lib/content";
import { submitLead } from "../lib/api";
import { Reveal, MaskReveal } from "../components/Reveal";
import { ArrowRight, ChevronRight } from "lucide-react";

const BUDGETS = ["< $25k", "$25k–$50k", "$50k–$100k", "$100k–$250k", "$250k–$1M", "$1M+", "Annual Retainer"];

const empty = {
  name: "", email: "", phone: "", company: "", title: "", website: "",
  stage: "", revenue: "", services: [], budget: "",
  achievements: "", referrer: "", why_us: "", message: "",
};

export default function Apply() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (key, name) => setForm((f) => ({
    ...f, [key]: f[key].includes(name) ? f[key].filter((x) => x !== name) : [...f[key], name],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email, and your story are required.");
      return;
    }
    try {
      setLoading(true);
      await submitLead(form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Application received. The desk reviews submissions personally.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-32 md:pt-40 pb-0 relative overflow-hidden" data-testid="apply-page">
      <Toaster richColors position="top-right" />

      {/* HERO */}
      <section className="container-edge">
        <div className="overline text-ink/60">By Invitation · Application 2026</div>
        <MaskReveal delay={0.05}>
          <h1 className="font-display uppercase text-7xl md:text-[12vw] leading-[0.82] mt-4 pb-3">
            Apply to be<br/><span className="serif-italic font-serif normal-case">a partner</span>.
          </h1>
        </MaskReveal>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-3xl text-lg md:text-2xl text-ink/80 font-light leading-snug">
            M Media Corp accepts a limited number of partnerships each season. Submissions are reviewed personally by Meghan Amaral and the senior partners. If your application advances, you will be invited to a 30-minute interview with the principal of the relevant division.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-ink/15 pt-10">
          {[
            { n: "01", t: "Submit", d: "Complete the application below — candidly. We read every word." },
            { n: "02", t: "Review", d: "If aligned, we extend an interview within 21 business days. Mutual fit is everything." },
            { n: "03", t: "Partner", d: "On acceptance, you receive a bespoke proposal and your dedicated team." },
          ].map((s) => (
            <div key={s.n}>
              <div className="overline text-ink/50">{s.n}</div>
              <h3 className="font-display uppercase text-4xl mt-2">{s.t}</h3>
              <p className="text-ink/70 mt-3">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* THANK YOU STATE */}
      {done ? (
        <section className="container-edge mt-20 pb-32">
          <div className="border-t border-b border-ink py-16" data-testid="apply-success">
            <div className="overline text-ink/55">Application — Received</div>
            <h2 className="font-display uppercase text-6xl md:text-8xl leading-[0.85] mt-4">
              Thank you.<br/><span className="serif-italic font-serif normal-case">Our desk has it.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-ink/75 text-lg leading-relaxed">
              Meghan personally reviews every application alongside our senior partners. Should we wish to advance you to interview, you will hear from a member of the team within 21 business days. In the rare case we cannot accept your application this season, we will tell you directly.
            </p>
            <div className="mt-10 flex gap-3">
              <Link to="/" className="btn-ghost" data-testid="apply-back-home">Return to index →</Link>
              <button onClick={() => { setDone(false); setForm(empty); }} className="btn-ink" data-testid="apply-reset">Submit another</button>
            </div>
          </div>
        </section>
      ) : (
        <form onSubmit={submit} className="container-edge mt-20 pb-32" data-testid="apply-form">
          {/* SECTION 01 — APPLICANT */}
          <Section number="01" title="The Applicant" subtitle="Who we are speaking to.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2">
              <Field label="Full name *" testid="apply-name"><input className="editorial-input" value={form.name} onChange={(e) => update("name", e.target.value)} required /></Field>
              <Field label="Title / Role" testid="apply-title"><input className="editorial-input" value={form.title} onChange={(e) => update("title", e.target.value)} /></Field>
              <Field label="Email *" testid="apply-email"><input type="email" className="editorial-input" value={form.email} onChange={(e) => update("email", e.target.value)} required /></Field>
              <Field label="Phone" testid="apply-phone"><input className="editorial-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} /></Field>
              <Field label="Brand / Company" testid="apply-company"><input className="editorial-input" value={form.company} onChange={(e) => update("company", e.target.value)} /></Field>
              <Field label="Website" testid="apply-website"><input className="editorial-input" placeholder="https://" value={form.website} onChange={(e) => update("website", e.target.value)} /></Field>
            </div>
          </Section>

          {/* SECTION 02 — STAGE */}
          <Section number="02" title="Where you stand" subtitle="So we route you to the right principal.">
            <ChipGroup label="Stage" testid="apply-stage" options={STAGES} value={form.stage} onChange={(v) => update("stage", v)} />
            <ChipGroup label="Annual revenue" testid="apply-revenue" options={REVENUES} value={form.revenue} onChange={(v) => update("revenue", v)} />
          </Section>

          {/* SECTION 03 — ENGAGEMENT */}
          <Section number="03" title="The engagement" subtitle="What you'd like us to build with you.">
            <MultiChipGroup label="Divisions of interest" testid="apply-services" options={SERVICES.map((s) => ({ k: s.slug, name: s.name }))} value={form.services} onToggle={(name) => toggle("services", name)} />
            <ChipGroup label="Investment range" testid="apply-budget" options={BUDGETS} value={form.budget} onChange={(v) => update("budget", v)} />
          </Section>

          {/* SECTION 04 — CANDIDACY */}
          <Section number="04" title="Your candidacy" subtitle="The parts a search engine cannot tell us.">
            <Field label="Notable achievements, press, or recognition (optional)" testid="apply-achievements">
              <textarea rows={4} className="editorial-input resize-none" value={form.achievements} onChange={(e) => update("achievements", e.target.value)} placeholder="Awards, fundraising rounds, marquee placements, exits, founding milestones…" />
            </Field>

            <ChipGroup label="How you found us" testid="apply-referrer" options={REFERRERS} value={form.referrer} onChange={(v) => update("referrer", v)} />

            <Field label="Why M Media — and why now? (optional)" testid="apply-why">
              <textarea rows={4} className="editorial-input resize-none" value={form.why_us} onChange={(e) => update("why_us", e.target.value)} placeholder="Tell us what drew you here. Specifics over flattery." />
            </Field>

            <Field label="Tell us your story * — what are we building?" testid="apply-message">
              <textarea rows={6} required className="editorial-input resize-none" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Brand, ambition, timeline, the cover story you want the world to read." />
            </Field>
          </Section>

          {/* SUBMIT */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6 items-center border-t border-ink/15 pt-10">
            <p className="md:col-span-7 overline text-ink/55 max-w-xl">
              Applications are confidential. By submitting you agree to be contacted by M Media Corp regarding your submission only. We do not share inquiries.
            </p>
            <div className="md:col-span-5 flex md:justify-end">
              <button type="submit" disabled={loading} className="btn-ink text-base px-8 py-4" data-testid="apply-submit">
                {loading ? "Submitting…" : <>Submit application <ArrowRight size={16} /></>}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* STUDIO BLOCK */}
      <section className="bg-ink text-cream">
        <div className="container-edge py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-7">
            <div className="overline text-cream/55">The Studio</div>
            <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.85] mt-3">
              {COMPANY.address.split(",")[0]}<br/>
              <span className="serif-italic font-serif normal-case text-cream/95">{COMPANY.address.split(",").slice(1).join(", ").trim()}</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex flex-col gap-2 md:items-end">
            <a href={`mailto:${COMPANY.email}`} className="overline hover:opacity-70" data-testid="apply-studio-email">{COMPANY.email}</a>
            <span className="overline text-cream/55">{COMPANY.social}</span>
          </div>
        </div>
      </section>
    </div>
  );
}

function Section({ number, title, subtitle, children }) {
  return (
    <Reveal>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-x-10 gap-y-8 border-t border-ink/15 pt-12">
        <div className="md:col-span-4">
          <div className="overline text-ink/55">Section {number}</div>
          <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.85] mt-3">{title}</h2>
          {subtitle && <p className="serif-italic font-serif text-2xl text-ink/70 mt-3">{subtitle}</p>}
        </div>
        <div className="md:col-span-8 flex flex-col gap-8">{children}</div>
      </div>
    </Reveal>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="overline text-ink/55">{label}</span>
      {children}
    </label>
  );
}

function ChipGroup({ label, options, value, onChange, testid }) {
  return (
    <div data-testid={testid}>
      <div className="overline text-ink/55 mb-3">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button type="button" key={opt} onClick={() => onChange(active ? "" : opt)}
              className={`overline px-4 py-2 border transition-all ${active ? "bg-ink text-cream border-ink" : "bg-transparent text-ink border-ink/30 hover:border-ink"}`}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MultiChipGroup({ label, options, value, onToggle, testid }) {
  return (
    <div data-testid={testid}>
      <div className="overline text-ink/55 mb-3">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = value.includes(opt.name);
          return (
            <button type="button" key={opt.k} onClick={() => onToggle(opt.name)}
              className={`overline px-4 py-2 border inline-flex items-center gap-2 transition-all ${active ? "bg-ink text-cream border-ink" : "bg-transparent text-ink border-ink/30 hover:border-ink"}`}>
              {opt.name} {active && <ChevronRight size={12} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
